import React, { type ChangeEvent, useMemo, useRef, useState } from 'react';

import omit from 'lodash/omit';
import { useSnackbar } from 'notistack';

import UploadFileIcon from '@mui/icons-material/UploadFile';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormGroup,
  LinearProgress,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from '@mui/material';

import { useGetAccountAssets } from '~/hooks/apis/offers/useGetAccountAssets';
import { useGetOffer } from '~/hooks/apis/offers/useGetOffer';

import { formatDateISO } from '~/services/TimeService';

import {
  useBlackoutsContext,
  useEgsDraftsContext,
  useInclusionDraftsContext,
  useOverridesContext,
  usePackageOptionsContext,
  usePricingToolSession,
  usePropertySetupContext,
  useSurchargeGridsContext,
} from '../PricingToolSessionContext';
import CopySectionOption from '../copyFromPrevious/CopySectionOption';
import type { CopySectionDetails } from '../copyFromPrevious/copyFromPreviousModel';
import { egsRooms } from '../tabs/egs/egsModel';
import { findReusableInclusionAsset } from '../tabs/inclusions/inclusionsModel';
import { DEFAULT_OPTION_STATE, collectRoomAssets } from '../tabs/packageOptions/packageOptionsModel';
import { replaceWithPreviousPackages } from '../tabs/packageOptions/previousDealOptions';
import type { RoomAssetLink } from '../tabs/packageOptions/types';
import { netRateFor } from '../tabs/propertySetup/propertySetupModel';
import { hasTypedCost } from '../tabs/surcharges/surchargeGridsModel';

import ImportInclusionsStep from './ImportInclusionsStep';
import ImportPropertySetupStep from './ImportPropertySetupStep';
import ImportRoomsStep from './ImportRoomsStep';
import {
  type CurrencyBoundSection,
  DEFAULT_PROPERTY_SETUP_PICKS,
  IMPORT_SECTIONS,
  IMPORT_STEP_LABELS,
  type ImportSectionKey,
  type ImportStep,
  type InclusionAssetChoices,
  type PropertySetupPicks,
  blackoutsDetails,
  currencyPickNote,
  currencySwitchWarnings,
  dealOptionsDetails,
  egsDetails,
  egsRoomAssets,
  hasPropertySetupToLoad,
  hasTypedNets,
  importReplaceWarning,
  importSteps,
  importedRoomTypes,
  inclusionRowsWithMissingAsset,
  inclusionsDetails,
  keepsOnEgsLoad,
  loadableEgs,
  loadableOverrides,
  loadableSurchargeGrid,
  loadedPropertySetupRows,
  loadsCosts,
  loadsRrp,
  missingAssetMessages,
  overridesDetails,
  propertySetupChanges,
  propertySetupDetails,
  propertySetupRows,
  reviewInclusionAssets,
  rrpNotLoadedReason,
  skipSectionsLabel,
  surchargesConfirmLines,
  surchargesDetails,
  tabHasGrid,
  toBlackoutFormValues,
  toEgsRecords,
  toImportedDealOptions,
  toInclusionDrafts,
  toOverrideSet,
  toSurchargePeriods,
} from './excelImportModel';
import { readWorkbook } from './readWorkbook';
import {
  type RoomChoices,
  prefillRoomChoices,
  resolveRoomAssets,
  roomTypesSharingAnAsset,
  roomTypesWithMissingAsset,
} from './roomMapping';
import type { ParsedWorkbook } from './types';

const ACCEPTED_FILES = '.xlsx,.xlsm';

type ExcelImportDialogProps = {
  offerId: string;
  open: boolean;
  onClose: () => void;
};

export default function ExcelImportDialog({ offerId, open, onClose }: ExcelImportDialogProps) {
  const { replaceBlackouts } = useBlackoutsContext();
  const { optionStates, replaceFirstOption, roomAssets, addRoomAsset } = usePackageOptionsContext();
  const { values: propertySetup, fx, applyImport, todayRateFor } = usePropertySetupContext();
  const { session: surchargeSession, replacePeriods, replaceGrids } = useSurchargeGridsContext();
  const { inclusionAssets, replaceOptionDrafts, linkAssetByName } = useInclusionDraftsContext();
  const { replaceOption: replaceOverrides } = useOverridesContext();
  const { drafts: egsDrafts, replaceDrafts: replaceEgs } = useEgsDraftsContext();
  const { enqueueSnackbar } = useSnackbar();
  const { data: offer, isLoading: isLoadingOffer, error: offerError } = useGetOffer(offerId);
  const {
    data: accountAssets,
    error: accountAssetsError,
    isFetching: isFetchingAssets,
  } = useGetAccountAssets(offer?.vendor_account_id, 'rooms', { enabled: open });
  const {
    data: accountServiceAssets,
    error: accountServiceAssetsError,
    isFetching: isFetchingServiceAssets,
  } = useGetAccountAssets(offer?.vendor_account_id, 'services', { enabled: open });

  const [fileName, setFileName] = useState<string | null>(null);
  const [isReading, setIsReading] = useState(false);
  const [readError, setReadError] = useState<string | null>(null);
  const [workbook, setWorkbook] = useState<ParsedWorkbook | null>(null);
  const [selected, setSelected] = useState<Set<ImportSectionKey>>(new Set());
  const [step, setStep] = useState<ImportStep>('workbook');
  const [pickedRooms, setPickedRooms] = useState<RoomChoices>({});
  const [leftOutInclusions, setLeftOutInclusions] = useState<Set<number>>(new Set());
  const [inclusionChoices, setInclusionChoices] = useState<InclusionAssetChoices>({});
  const [propertySetupPicks, setPropertySetupPicks] = useState<PropertySetupPicks>(DEFAULT_PROPERTY_SETUP_PICKS);
  // A read still running when the dialog closes is dropped, so it cannot overwrite a file picked after reopening.
  const latestReadRef = useRef(0);

  const { draftIdBySfid } = usePricingToolSession();
  const assets = useMemo(
    () => collectRoomAssets(accountAssets ?? [], draftIdBySfid, roomAssets),
    [accountAssets, draftIdBySfid, roomAssets],
  );
  const roomTypes = useMemo(() => importedRoomTypes(workbook?.dealOptions ?? []), [workbook]);
  const prefilledRooms = useMemo(() => prefillRoomChoices(roomTypes, assets), [roomTypes, assets]);
  const roomsMissingAsset = roomTypesWithMissingAsset(pickedRooms, assets);
  const roomChoices = { ...prefilledRooms, ...omit(pickedRooms, roomsMissingAsset) };
  const sharingAnAsset = roomTypesSharingAnAsset(roomChoices);
  const inclusionAccountAssets = useMemo(
    () => collectRoomAssets(accountServiceAssets ?? [], draftIdBySfid),
    [accountServiceAssets, draftIdBySfid],
  );
  const includedInclusions = workbook?.inclusions.filter(({ row }) => !leftOutInclusions.has(row)) ?? [];
  const inclusionsMissingAsset = inclusionRowsWithMissingAsset(
    inclusionChoices,
    inclusionAssets,
    inclusionAccountAssets,
  );
  const loadsPropertySetup = selected.has('propertySetup');
  const propertySetupFields =
    workbook?.layout && propertySetup
      ? propertySetupRows(workbook.propertySetup, propertySetup, propertySetupPicks, todayRateFor)
      : [];
  const propertySetupLoad = loadsPropertySetup ? propertySetupChanges(propertySetupFields, propertySetupPicks) : {};
  // Every other section loads against Property Setup as it will be after this import.
  const pickedPropertySetup = propertySetup ? { ...propertySetup, ...propertySetupLoad } : null;
  const currency = propertySetupLoad.currency ?? fx.currency;
  // The first step shows what loads with every Property Setup field ticked, so going Back to it never blocks the
  // step where the PCM can tick the currency again.
  const shownCurrency =
    step === 'workbook' && loadsPropertySetup
      ? (propertySetupChanges(propertySetupFields, DEFAULT_PROPERTY_SETUP_PICKS).currency ?? fx.currency)
      : currency;
  const overrides = workbook ? loadableOverrides(workbook, shownCurrency) : null;
  const surchargeGrid = workbook ? loadableSurchargeGrid(workbook, shownCurrency) : null;
  const loadsDealOptions = selected.has('dealOptions');
  const loadsEgs = selected.has('extraGuestSurcharges');
  // With Deal Options, Option 1's rooms become the Rooms step's picks; otherwise they stay the session's.
  const egsAssets = egsRoomAssets(
    workbook?.extraGuestSurcharges.map(({ room }) => room) ?? [],
    // Rooms the Rooms step creates only get an id on Load; the preview just needs to know which ones it covers.
    loadsDealOptions
      ? Object.fromEntries(roomTypes.map((roomType) => [roomType, { id: roomChoices[roomType], name: roomType }]))
      : null,
    egsRooms((optionStates[0] ?? DEFAULT_OPTION_STATE).roomsByCategory),
  );
  const egsRate = netRateFor(pickedPropertySetup);
  const egsToLoad = workbook ? loadableEgs(workbook, shownCurrency).filter(({ room }) => egsAssets[room]) : [];

  const layoutIssues = workbook?.issues.filter((issue) => issue.section === 'layout') ?? [];
  const sectionDetails: Record<ImportSectionKey, CopySectionDetails | undefined> = {
    propertySetup:
      workbook?.layout && propertySetup
        ? propertySetupDetails(workbook, propertySetupFields, propertySetupPicks)
        : undefined,
    dealOptions: workbook?.layout ? dealOptionsDetails(workbook, shownCurrency) : undefined,
    inclusions: workbook?.layout ? inclusionsDetails(workbook, shownCurrency, leftOutInclusions) : undefined,
    overrides: workbook?.layout ? overridesDetails(workbook, shownCurrency) : undefined,
    surcharges: workbook?.layout
      ? surchargesDetails(workbook, pickedPropertySetup, formatDateISO(), {
          sessionCurrency: shownCurrency,
          keepsTabGrid: tabHasGrid(surchargeSession),
        })
      : undefined,
    extraGuestSurcharges: workbook?.layout
      ? egsDetails(workbook, shownCurrency, egsAssets, { fromDealOptions: loadsDealOptions, hasRate: !!egsRate })
      : undefined,
    blackouts: workbook?.layout ? blackoutsDetails(workbook, pickedPropertySetup, formatDateISO()) : undefined,
  };
  const confirmLines: Partial<Record<ImportSectionKey, Array<string>>> = {
    surcharges: workbook?.layout ? surchargesConfirmLines(workbook) : [],
  };
  const hasContent: Record<ImportSectionKey, boolean> = {
    propertySetup: hasPropertySetupToLoad(propertySetupFields),
    dealOptions: (workbook?.dealOptions.length ?? 0) > 0,
    inclusions: (workbook?.inclusions.length ?? 0) > 0,
    overrides: !!overrides,
    surcharges: (workbook?.surchargePeriods.length ?? 0) > 0 || !!surchargeGrid,
    extraGuestSurcharges: egsToLoad.length > 0 && !!egsRate,
    blackouts: (workbook?.blackouts.length ?? 0) > 0,
  };
  const loadsInclusions = selected.has('inclusions');
  const loadsOverrides = selected.has('overrides');
  // Rooms are matched against the account's, so they must have loaded or every room would be created again.
  const isLoadingRooms = isLoadingOffer || isFetchingAssets;
  const roomsError = offerError || accountAssetsError;
  // Until Property Setup loads its currency reads AUD, which would let costs in another currency through.
  const dealOptionsReady = !isLoadingRooms && !roomsError && !fx.isLoading;
  // Inclusions reuse the account's assets by name, and RRP only loads in an AUD session.
  const isLoadingInclusionAssets = isLoadingOffer || isFetchingServiceAssets;
  const inclusionAssetsError = offerError || accountServiceAssetsError;
  const inclusionsReady = !isLoadingInclusionAssets && !inclusionAssetsError && !fx.isLoading;
  const sectionsReady =
    selected.size > 0 &&
    [...selected].every((section) => hasContent[section]) &&
    (!loadsDealOptions || dealOptionsReady) &&
    (!loadsInclusions || inclusionsReady) &&
    // Vendor revenue tiers and extra guest costs only load in the workbook's currency, which reads AUD until
    // Property Setup loads.
    (!(loadsOverrides || loadsEgs) || !fx.isLoading);
  // Unticking Currency on Property Setup is the one pick that can stop a section picked on the first step from loading.
  const needCurrency: Array<CurrencyBoundSection> =
    step === 'propertySetup' && workbook
      ? [
          ...(loadsOverrides && !overrides ? ['overrides' as const] : []),
          ...(loadsEgs && !loadsCosts(workbook, currency) ? ['extraGuestSurcharges' as const] : []),
        ]
      : [];
  const steps = importSteps(selected);
  const stepIndex = steps.indexOf(step);
  const stepComplete: Record<ImportStep, boolean> = {
    workbook: sectionsReady,
    propertySetup: loadedPropertySetupRows(propertySetupFields, propertySetupPicks).length > 0,
    rooms: sharingAnAsset.size === 0,
    inclusions: includedInclusions.length > 0,
    confirm: true,
  };
  const replacesGrid = selected.has('surcharges') && !!surchargeGrid;
  const warning = importReplaceWarning(selected, replacesGrid);
  const missingAssets = missingAssetMessages(
    loadsDealOptions ? roomsMissingAsset : [],
    loadsInclusions
      ? includedInclusions.filter(({ row }) => inclusionsMissingAsset.includes(row)).map(({ name }) => name)
      : [],
  );
  const switchWarnings = pickedPropertySetup
    ? currencySwitchWarnings(propertySetup, pickedPropertySetup, {
        // The import's own grid replaces those costs, so the switch takes nothing the PCM typed.
        hasTypedSurchargeCosts: hasTypedCost(surchargeSession) && !replacesGrid,
        // Deal Options replaces Option 1, so only the other options keep what was typed.
        keepsNets: hasTypedNets(
          Object.entries(optionStates)
            .filter(([option]) => !loadsDealOptions || Number(option) !== 0)
            .map(([, state]) => state),
        ),
        keepsEgsCosts: egsDrafts.some((draft) => (!loadsEgs || keepsOnEgsLoad(draft)) && draft.costPrice > 0),
        rrpReason: loadsInclusions && workbook ? rrpNotLoadedReason(workbook, currency) : null,
      })
    : [];

  function resetChoices() {
    setSelected(new Set());
    setStep('workbook');
    setPickedRooms({});
    setLeftOutInclusions(new Set());
    setInclusionChoices({});
    setPropertySetupPicks(DEFAULT_PROPERTY_SETUP_PICKS);
  }

  function handleContinue() {
    // Stores every choice, not just the changed ones, so a refetch after review cannot move a room.
    if (step === 'rooms') {
      setPickedRooms(roomChoices);
    }
    if (step === 'inclusions') {
      setInclusionChoices(reviewInclusionAssets(includedInclusions, inclusionAssets, inclusionAccountAssets));
    }
    setStep(steps[stepIndex + 1]);
  }

  async function handleFileChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    // Cleared so picking the same file again after editing it still fires a change.
    event.target.value = '';
    if (!file) {
      return;
    }

    setFileName(file.name);
    setWorkbook(null);
    setReadError(null);
    resetChoices();
    setIsReading(true);
    const readId = ++latestReadRef.current;
    let read: ParsedWorkbook | null = null;
    let error: string | null = null;
    try {
      read = await readWorkbook(file);
    } catch {
      error = `Couldn't read ${file.name}. Check it's a Pricing Tool workbook saved as .xlsx or .xlsm.`;
    }
    if (readId !== latestReadRef.current) {
      return;
    }
    setIsReading(false);
    setWorkbook(read);
    setReadError(error);
  }

  function handleSectionChange(section: ImportSectionKey, checked: boolean) {
    setSelected((current) => {
      const next = new Set(current);
      if (checked) {
        next.add(section);
      } else {
        next.delete(section);
      }
      return next;
    });
  }

  function handleLoad() {
    // First, so the currency and travel dates the other sections were checked against are in place.
    if (loadsPropertySetup) {
      applyImport(propertySetupLoad);
    }
    let firstOption = optionStates[0] ?? DEFAULT_OPTION_STATE;
    let dealOptionAssets: Record<string, RoomAssetLink> | null = null;
    if (loadsDealOptions) {
      const { assetByRoomType, created } = resolveRoomAssets(roomTypes, roomChoices, assets);
      dealOptionAssets = assetByRoomType;
      created.forEach(addRoomAsset);
      const imported = toImportedDealOptions(workbook.dealOptions, assetByRoomType, loadsCosts(workbook, currency));
      firstOption = replaceWithPreviousPackages(firstOption, imported);
      replaceFirstOption(firstOption);
    }
    if (loadsInclusions) {
      const drafts = toInclusionDrafts(includedInclusions, loadsRrp(currency)).map((draft, index) => {
        const reviewed = inclusionAccountAssets.find(
          ({ id }) => id === inclusionChoices[includedInclusions[index].row],
        );
        return { ...draft, assetLink: linkAssetByName(draft.name, reviewed ? [reviewed] : []) };
      });
      replaceOptionDrafts(0, drafts);
    }
    if (loadsOverrides) {
      replaceOverrides(0, toOverrideSet(overrides));
    }
    if (selected.has('surcharges')) {
      replacePeriods(toSurchargePeriods(workbook.surchargePeriods, workbook.propertySetup.currency, currency));
      if (surchargeGrid) {
        // The session's own currency, unset included, not the AUD fallback: the grid hook compares against it.
        replaceGrids(
          surchargeGrid.grids,
          surchargeGrid.costs,
          propertySetupLoad.currency ?? propertySetup?.currency ?? undefined,
        );
      }
    }
    // After Deal Options, whose rooms these records go on.
    if (loadsEgs) {
      const optionRooms = egsRooms(firstOption.roomsByCategory);
      const roomAssetsAtLoad = egsRoomAssets(
        workbook.extraGuestSurcharges.map(({ room }) => room),
        dealOptionAssets,
        optionRooms,
      );
      const records = toEgsRecords(loadableEgs(workbook, currency), roomAssetsAtLoad, currency, egsRate);
      replaceEgs(records, keepsOnEgsLoad);
    }
    if (selected.has('blackouts')) {
      replaceBlackouts(toBlackoutFormValues(workbook.blackouts));
    }
    enqueueSnackbar(`Loaded from ${fileName}`, { variant: 'success' });
    onClose();
  }

  function handleExited() {
    latestReadRef.current += 1;
    setIsReading(false);
    setFileName(null);
    setWorkbook(null);
    setReadError(null);
    resetChoices();
  }

  return (
    <Dialog
      fullWidth
      maxWidth={step === 'rooms' || step === 'inclusions' ? 'lg' : 'md'}
      onClose={onClose}
      open={open}
      slotProps={{ transition: { onExited: handleExited } }}
    >
      <DialogTitle>Upload Excel</DialogTitle>
      <DialogContent dividers>
        <Stepper activeStep={stepIndex} sx={{ mb: 3 }}>
          {steps.map((importStep) => (
            <Step key={importStep}>
              <StepLabel>{IMPORT_STEP_LABELS[importStep]}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {step === 'workbook' && (
          <Stack spacing={2}>
            <Stack alignItems="center" direction="row" spacing={2}>
              <Button component="label" disabled={isReading} startIcon={<UploadFileIcon />} variant="outlined">
                Choose workbook
                <input accept={ACCEPTED_FILES} hidden onChange={handleFileChange} type="file" />
              </Button>
              <Typography color="text.secondary" variant="body2">
                {fileName ?? 'Pricing Tool workbook, .xlsx or .xlsm'}
              </Typography>
            </Stack>

            {isReading && <LinearProgress />}

            {readError && <Alert severity="error">{readError}</Alert>}

            {layoutIssues.map((issue) => (
              <Alert key={issue.message} severity="error">
                {issue.message}
              </Alert>
            ))}

            {workbook?.layout && (
              <>
                <Typography color="text.secondary" variant="body2">
                  {`Loads from the "${workbook.layout.primarySheet}" sheet.`}
                </Typography>
                <FormGroup>
                  {IMPORT_SECTIONS.map((section) => (
                    <CopySectionOption
                      checked={selected.has(section.key)}
                      details={sectionDetails[section.key]}
                      key={section.key}
                      label={section.label}
                      name={section.key}
                      onChange={(event) => handleSectionChange(section.key, event.target.checked)}
                    />
                  ))}
                </FormGroup>
                {((loadsDealOptions && isLoadingRooms) ||
                  (loadsInclusions && isLoadingInclusionAssets) ||
                  ((loadsPropertySetup || loadsDealOptions || loadsInclusions || loadsOverrides || loadsEgs) &&
                    fx.isLoading)) && <CircularProgress size={24} />}
                {loadsDealOptions && roomsError && (
                  <Alert severity="error">
                    Unable to load this property&apos;s rooms, so deal options can&apos;t be matched to them.
                  </Alert>
                )}
                {loadsInclusions && inclusionAssetsError && (
                  <Alert severity="error">
                    Unable to load this property&apos;s inclusion assets, so inclusions can&apos;t be matched to them.
                  </Alert>
                )}
              </>
            )}
          </Stack>
        )}

        {step === 'propertySetup' && (
          <ImportPropertySetupStep
            onFxSource={(fxSource) => setPropertySetupPicks((current) => ({ ...current, fxSource }))}
            onToggle={(field, checked) =>
              setPropertySetupPicks((current) => ({ ...current, fields: { ...current.fields, [field]: checked } }))
            }
            picks={propertySetupPicks}
            rows={propertySetupFields}
          />
        )}
        {needCurrency.length > 0 && (
          <Alert
            action={
              <Button
                color="inherit"
                onClick={() => needCurrency.forEach((section) => handleSectionChange(section, false))}
                size="small"
                sx={{ whiteSpace: 'nowrap' }}
                variant="outlined"
              >
                {skipSectionsLabel(needCurrency)}
              </Button>
            }
            severity="warning"
            sx={{ mt: 2, alignItems: 'center' }}
          >
            {currencyPickNote(workbook, currency, needCurrency)}
          </Alert>
        )}

        {step === 'rooms' && workbook && (
          <ImportRoomsStep
            assets={assets}
            choices={roomChoices}
            dealOptions={workbook.dealOptions}
            onChange={(roomType, assetId) => setPickedRooms((current) => ({ ...current, [roomType]: assetId }))}
            roomTypes={roomTypes}
            sharingAnAsset={sharingAnAsset}
          />
        )}

        {step === 'inclusions' && workbook && (
          <ImportInclusionsStep
            inclusions={workbook.inclusions}
            leftOutRows={leftOutInclusions}
            onToggle={(row, included) =>
              setLeftOutInclusions((current) => {
                const next = new Set(current);
                if (included) {
                  next.delete(row);
                } else {
                  next.add(row);
                }
                return next;
              })
            }
            reusesAsset={(name) => !!findReusableInclusionAsset(name, inclusionAssets, inclusionAccountAssets)}
            rrpNotLoaded={rrpNotLoadedReason(workbook, currency)}
            withRrp={loadsRrp(currency)}
          />
        )}

        {step === 'confirm' && (
          <>
            <Typography variant="body2">From {fileName}:</Typography>
            <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
              {IMPORT_SECTIONS.filter((section) => selected.has(section.key)).map((section) => (
                <Typography component="li" key={section.key} variant="body2">
                  {`${section.label}: ${sectionDetails[section.key]?.summary}`}
                  {confirmLines[section.key]?.length > 0 && (
                    <Box component="ul" sx={{ m: 0, pl: 2 }}>
                      {confirmLines[section.key].map((line) => (
                        <Typography color="text.secondary" component="li" key={line} variant="body2">
                          {line}
                        </Typography>
                      ))}
                    </Box>
                  )}
                </Typography>
              ))}
            </Box>
            {warning && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                {warning}
              </Alert>
            )}
            {switchWarnings.map((message) => (
              <Alert key={message} severity="warning" sx={{ mt: 2 }}>
                {message}
              </Alert>
            ))}
            {missingAssets.map((message) => (
              <Alert key={message} severity="error" sx={{ mt: 2 }}>
                {message}
              </Alert>
            ))}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="text">
          Cancel
        </Button>
        {stepIndex > 0 && (
          <Button onClick={() => setStep(steps[stepIndex - 1])} variant="text">
            Back
          </Button>
        )}
        {step === 'confirm' ? (
          <Button disabled={!sectionsReady || missingAssets.length > 0} onClick={handleLoad} variant="contained">
            Load
          </Button>
        ) : (
          <Button disabled={!sectionsReady || !stepComplete[step]} onClick={handleContinue} variant="contained">
            Continue
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
